import { useState, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { jsPDF } from "jspdf";
import Viewer3D from "@/components/Viewer3D";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/* ---------- OPTION SETS ---------- */
const PROFILES = [
  { key: "W20", label: "W20 — Ultra-slim internal steel" },
  { key: "W40", label: "W40 — Traditional steel profile" },
  { key: "ALES65", label: "ALES65 — Thermally broken aluminium" },
];

const DOOR_TYPES = [
  { key: "Single", label: "Single Leaf" },
  { key: "Double", label: "Double Leaf" },
];

const PANEL_STYLES = [
  { key: "Horizontal Lites (3 Equal)", label: "3 Equal Horizontal Lites" },
  { key: "Horizontal Lites (3 - Small Middle)", label: "3 Lites · Small Middle" },
  { key: "Horizontal Lites (4 Equal)", label: "4 Equal Horizontal Lites" },
  { key: "Grid Pattern (2x2)", label: "Grid Pattern · 2×2" },
  { key: "Grid Pattern (3x2)", label: "Grid Pattern · 3×2" },
];

/* Eurosteel Signature Finishes (matches /data/finishes.js) — image swatches */
const EUROSTEEL_SIGNATURE = [
  { key: "Eurosteel 1#", label: "1#", image: "/finishes/eurosteel/eurosteel-1.webp" },
  { key: "Eurosteel 3#", label: "3#", image: "/finishes/eurosteel/eurosteel-3.webp" },
  { key: "Eurosteel 4#", label: "4#", image: "/finishes/eurosteel/eurosteel-4.webp" },
  { key: "Eurosteel 5#", label: "5#", image: "/finishes/eurosteel/eurosteel-5.webp" },
  { key: "Eurosteel 6#", label: "6#", image: "/finishes/eurosteel/eurosteel-6.webp" },
  { key: "Eurosteel 7#", label: "7#", image: "/finishes/eurosteel/eurosteel-7.webp" },
  { key: "Eurosteel 8#", label: "8#", image: "/finishes/eurosteel/eurosteel-8.webp" },
  { key: "Eurosteel 9#", label: "9#", image: "/finishes/eurosteel/eurosteel-9.webp" },
  { key: "Eurosteel 10#", label: "10#", image: "/finishes/eurosteel/eurosteel-10.webp" },
  { key: "Eurosteel 11#", label: "11#", image: "/finishes/eurosteel/eurosteel-11.webp" },
  { key: "Eurosteel 12#", label: "12#", image: "/finishes/eurosteel/eurosteel-12.webp" },
];

/* Dulux Powdercoat basics — the original configurator palette */
const DULUX_BASICS = [
  { key: "Black", hex: "#0A0A0A" },
  { key: "White", hex: "#FAFAFA" },
  { key: "Charcoal", hex: "#575F66" },
  { key: "Flaxpod", hex: "#363636" },
  { key: "Bronze", hex: "#8B6F47" },
  { key: "Desert Sand", hex: "#A6937E" },
  { key: "Soft Pink", hex: "#E8C4C4" },
];

const HARDWARE = [
  { key: "Lever Handle (Standard)", label: "Lever Handle · Standard" },
  { key: "Pull Rod Handle", label: "Pull Rod Handle · Vertical" },
  { key: "Half Circle Pull Handle", label: "Half-Circle Pull Handle" },
  { key: "No Hardware", label: "No Hardware" },
];

const GLASS_TYPES = [
  { key: "Clear", label: "Clear" },
  { key: "Frosted", label: "Frosted" },
  { key: "Fluted", label: "Fluted (Reeded)" },
  { key: "Aquatex", label: "Aquatex" },
  { key: "Rain", label: "Rain" },
  { key: "Flemish", label: "Flemish" },
];

const YES_NO = [
  { key: "No", label: "No" },
  { key: "Yes", label: "Yes" },
];

const DEFAULTS = {
  profile: "W20",
  width: 1200,
  height: 2200,
  color: "Black",
  hardware: "Lever Handle (Standard)",
  glass_type: "Clear",
  door_type: "Double",
  panel_style: "Horizontal Lites (3 Equal)",
  lites_count: 3,
  transom: "No",
  transomHeight: 400,
  threshold: "Standard Threshold",
  sidelite_left: "No",
  sidelite_right: "No",
  arched_in_square: "No",
  true_arch_frame: "No",
};

function sanitize(cfg) {
  const c = { ...cfg };
  if (c.true_arch_frame === "Yes") {
    c.arched_in_square = "No";
    c.transom = "No";
  }
  return c;
}

/* ---------- UI PRIMITIVES ---------- */
const FieldLabel = ({ children }) => (
  <p className="font-tech text-[11px] font-semibold uppercase tracking-[0.25em] text-[#0a0a0c]">
    {children}
  </p>
);

const CategoryTitle = ({ eyebrow, title }) => (
  <div className="mb-5">
    <p className="font-tech text-[10px] font-semibold uppercase tracking-[0.4em] text-[#c6a87c]">
      {eyebrow}
    </p>
    <h3 className="mt-1 font-serif-display text-2xl font-bold text-[#0a0a0c]">{title}</h3>
  </div>
);

const StyledSelect = ({ value, onChange, options, testid, placeholder }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger
      data-testid={testid}
      className="h-11 w-full rounded-2xl border border-black/15 bg-white px-4 font-body text-[13px] font-medium text-[#0a0a0c] shadow-sm transition-all duration-300 hover:border-[#c6a87c] focus:border-[#c6a87c] focus:ring-2 focus:ring-[#c6a87c]/30"
    >
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent className="max-h-[320px] rounded-xl border-black/15 bg-white text-[#0a0a0c]">
      {options.map((o) => (
        <SelectItem
          key={o.key}
          value={o.key}
          data-testid={`${testid}-option-${o.key}`}
          className="cursor-pointer font-body text-[13px] text-[#0a0a0c] focus:bg-[#c6a87c]/10 focus:text-[#0a0a0c]"
        >
          {o.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const NumberField = ({
  value,
  onChange,
  min,
  max,
  step = 1,
  buttonStep = 50,
  testid,
}) => (
  <div className="flex items-center gap-2 rounded-2xl border border-black/15 bg-white px-2 py-1.5 shadow-sm transition-all duration-300 focus-within:border-[#c6a87c] focus-within:ring-2 focus-within:ring-[#c6a87c]/30 hover:border-[#c6a87c]">
    <button
      type="button"
      onClick={() => onChange(Math.max(min, value - buttonStep))}
      className="h-8 w-8 rounded-full font-serif-display text-xl font-bold text-[#0a0a0c] transition-colors hover:bg-black/5"
      data-testid={`${testid}-decrement`}
    >
      −
    </button>
    <input
      type="number"
      value={value}
      min={min}
      max={max}
      step={step}
      inputMode="numeric"
      onChange={(e) => {
        // Allow fully free-form entry while typing — only cap at the hard max
        // so the browser doesn't block any-mm input. Lower bound is enforced
        // on blur below so a user can still clear and retype without the
        // field snapping back to `min` on the first keystroke.
        const raw = e.target.value;
        if (raw === "") {
          onChange("");
          return;
        }
        const n = Number(raw);
        if (Number.isNaN(n)) return;
        onChange(Math.min(max, n));
      }}
      onBlur={(e) => {
        const n = Number(e.target.value);
        if (!Number.isFinite(n) || n < min) onChange(min);
      }}
      className="w-full bg-transparent text-center font-serif-display text-lg font-bold text-[#0a0a0c] focus:outline-none"
      data-testid={testid}
    />
    <span className="pointer-events-none pr-1 font-tech text-[10px] font-semibold uppercase tracking-[0.25em] text-[#0a0a0c]/60">
      mm
    </span>
    <button
      type="button"
      onClick={() => onChange(Math.min(max, value + buttonStep))}
      className="h-8 w-8 rounded-full font-serif-display text-xl font-bold text-[#0a0a0c] transition-colors hover:bg-black/5"
      data-testid={`${testid}-increment`}
    >
      +
    </button>
  </div>
);

/* ---------- CATEGORY CARDS ---------- */
const CardBase =
  "rounded-[28px] border border-black/10 p-6 shadow-[0_20px_60px_-40px_rgba(10,10,12,0.35)] lg:p-8";

function StructureCard({ cfg, set }) {
  return (
    <div className={`${CardBase} bg-gradient-to-br from-white via-white to-[#faf6ec]`} data-testid="cat-structure">
      <CategoryTitle eyebrow="01 · Structure" title="Frame & Size." />
      <div className="grid grid-cols-1 gap-4">
        <div className="space-y-2">
          <FieldLabel>Profile Type</FieldLabel>
          <StyledSelect value={cfg.profile} onChange={set("profile")} options={PROFILES} testid="opt-profile" />
        </div>
        <div className="space-y-2">
          <FieldLabel>Door Type</FieldLabel>
          <StyledSelect value={cfg.door_type} onChange={set("door_type")} options={DOOR_TYPES} testid="opt-door-type" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <FieldLabel>Width</FieldLabel>
            <NumberField value={cfg.width} onChange={set("width")} min={600} max={3600} buttonStep={50} testid="opt-width" />
          </div>
          <div className="space-y-2">
            <FieldLabel>Height</FieldLabel>
            <NumberField value={cfg.height} onChange={set("height")} min={1800} max={3600} buttonStep={50} testid="opt-height" />
          </div>
        </div>
      </div>
    </div>
  );
}

function AdditionsCard({ cfg, set }) {
  const OptionPill = ({ active, disabled, onClick, testid, children }) => (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      data-testid={testid}
      disabled={disabled}
      className={`relative rounded-full border px-4 py-2 font-tech text-[11px] font-semibold uppercase tracking-[0.18em] transition-all duration-300 focus:outline-none ${
        disabled
          ? "cursor-not-allowed border-black/10 bg-black/5 text-[#0a0a0c]/30 line-through"
          : active
          ? "border-[#c6a87c] bg-[#c6a87c]/15 text-[#0a0a0c] shadow-[0_0_0_1px_#c6a87c,0_8px_24px_-12px_rgba(198,168,124,0.6)]"
          : "border-black/15 bg-white text-[#0a0a0c] hover:border-[#0a0a0c]/50"
      }`}
    >
      {children}
    </button>
  );

  const trueArchOn = cfg.true_arch_frame === "Yes";

  return (
    <div className={`${CardBase} bg-gradient-to-br from-white via-white to-[#eef4f2]`} data-testid="cat-additions">
      <CategoryTitle eyebrow="02 · Additions" title="Sidelites, Transom & Arch." />
      <div className="grid grid-cols-1 gap-5">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <FieldLabel>Left Sidelite</FieldLabel>
            <div className="flex gap-2">
              {YES_NO.map((o) => (
                <OptionPill
                  key={o.key}
                  active={cfg.sidelite_left === o.key}
                  onClick={() => set("sidelite_left")(o.key)}
                  testid={`opt-sidelite-left-${o.key}`}
                >
                  {o.label}
                </OptionPill>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <FieldLabel>Right Sidelite</FieldLabel>
            <div className="flex gap-2">
              {YES_NO.map((o) => (
                <OptionPill
                  key={o.key}
                  active={cfg.sidelite_right === o.key}
                  onClick={() => set("sidelite_right")(o.key)}
                  testid={`opt-sidelite-right-${o.key}`}
                >
                  {o.label}
                </OptionPill>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <FieldLabel>Transom Window</FieldLabel>
          <div className="flex gap-2">
            {YES_NO.map((o) => (
              <OptionPill
                key={o.key}
                active={cfg.transom === o.key}
                onClick={() => set("transom")(o.key)}
                testid={`opt-transom-${o.key}`}
                disabled={o.key === "Yes" && trueArchOn}
              >
                {o.label}
              </OptionPill>
            ))}
          </div>
          {cfg.transom === "Yes" && (
            <div className="mt-3">
              <p className="mb-2 font-tech text-[10px] font-semibold uppercase tracking-[0.25em] text-[#0a0a0c]/70">
                Transom Height
              </p>
              <NumberField
                value={cfg.transomHeight}
                onChange={set("transomHeight")}
                min={200}
                max={800}
                buttonStep={50}
                testid="opt-transom-height"
              />
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <FieldLabel>Arched in Square</FieldLabel>
            <div className="flex gap-2">
              {YES_NO.map((o) => (
                <OptionPill
                  key={o.key}
                  active={cfg.arched_in_square === o.key}
                  onClick={() => set("arched_in_square")(o.key)}
                  testid={`opt-arched-sq-${o.key}`}
                  disabled={o.key === "Yes" && trueArchOn}
                >
                  {o.label}
                </OptionPill>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <FieldLabel>True Arch Frame</FieldLabel>
            <div className="flex gap-2">
              {YES_NO.map((o) => (
                <OptionPill
                  key={o.key}
                  active={cfg.true_arch_frame === o.key}
                  onClick={() => set("true_arch_frame")(o.key)}
                  testid={`opt-true-arch-${o.key}`}
                >
                  {o.label}
                </OptionPill>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function GlazingCard({ cfg, set }) {
  return (
    <div className={`${CardBase} bg-gradient-to-br from-white via-white to-[#f2eff7]`} data-testid="cat-glazing">
      <CategoryTitle eyebrow="03 · Glazing" title="Panel Style & Glass." />
      <div className="grid grid-cols-1 gap-4">
        <div className="space-y-2">
          <FieldLabel>Panel Style</FieldLabel>
          <StyledSelect value={cfg.panel_style} onChange={set("panel_style")} options={PANEL_STYLES} testid="opt-panel-style" />
        </div>
        <div className="space-y-2">
          <FieldLabel>Glass Type</FieldLabel>
          <StyledSelect value={cfg.glass_type} onChange={set("glass_type")} options={GLASS_TYPES} testid="opt-glass" />
        </div>
      </div>
    </div>
  );
}

function FinishCard({ cfg, set }) {
  const isEurosteel = EUROSTEEL_SIGNATURE.some((c) => c.key === cfg.color);
  const isDulux = DULUX_BASICS.some((c) => c.key === cfg.color);
  const displayName = isEurosteel
    ? `Eurosteel Signature ${EUROSTEEL_SIGNATURE.find((c) => c.key === cfg.color)?.label}`
    : cfg.color;

  return (
    <div className={`${CardBase} bg-gradient-to-br from-white via-white to-[#f7ede2]`} data-testid="cat-finish">
      <CategoryTitle eyebrow="04 · Finish" title="Colour & Hardware." />
      <div className="grid grid-cols-1 gap-6">
        {/* Eurosteel Signature */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <FieldLabel>Eurosteel Signature Finishes</FieldLabel>
            {isEurosteel && (
              <span className="font-body text-[12px] font-medium text-[#0a0a0c]">{displayName}</span>
            )}
          </div>
          <div className="grid grid-cols-6 gap-2 sm:grid-cols-8 md:grid-cols-11">
            {EUROSTEEL_SIGNATURE.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => set("color")(c.key)}
                data-testid={`opt-colour-${c.key.replace(/[^a-z0-9]+/gi, "-")}`}
                title={`Eurosteel Signature ${c.label}`}
                className={`group relative aspect-square overflow-hidden rounded-xl transition-all duration-300 ${
                  cfg.color === c.key
                    ? "ring-2 ring-[#c6a87c] ring-offset-2 ring-offset-white"
                    : "ring-1 ring-black/15 hover:ring-2 hover:ring-[#0a0a0c]/40 hover:ring-offset-2"
                }`}
              >
                <img src={c.image} alt={`Eurosteel ${c.label}`} className="h-full w-full object-cover" />
                <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-black/45 py-[2px] text-center font-tech text-[9px] font-semibold uppercase tracking-[0.14em] text-white">
                  {c.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Dulux Basics */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <FieldLabel>Dulux Powdercoat · Basics</FieldLabel>
            {isDulux && (
              <span className="font-body text-[12px] font-medium text-[#0a0a0c]">{cfg.color}</span>
            )}
          </div>
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 md:grid-cols-7">
            {DULUX_BASICS.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => set("color")(c.key)}
                data-testid={`opt-colour-${c.key.replace(/[^a-z0-9]+/gi, "-")}`}
                title={c.key}
                className={`group relative aspect-square rounded-xl transition-all duration-300 ${
                  cfg.color === c.key
                    ? "ring-2 ring-[#c6a87c] ring-offset-2 ring-offset-white"
                    : "ring-1 ring-black/15 hover:ring-2 hover:ring-[#0a0a0c]/40 hover:ring-offset-2"
                }`}
                style={{
                  background: `linear-gradient(135deg, ${c.hex} 0%, ${c.hex} 55%, rgba(255,255,255,0.28) 100%)`,
                }}
              />
            ))}
          </div>
          <p className="font-body text-[11px] text-[#0a0a0c]/70">
            Any colour from the Dulux Powdercoat range is available — visit the{" "}
            <Link to="/finishes" className="underline decoration-[#c6a87c] underline-offset-2 hover:text-[#c6a87c]">
              full finishes page
            </Link>{" "}
            for the complete palette.
          </p>
        </div>

        <div className="space-y-2">
          <FieldLabel>Hardware</FieldLabel>
          <StyledSelect value={cfg.hardware} onChange={set("hardware")} options={HARDWARE} testid="opt-hardware" />
        </div>
      </div>
    </div>
  );
}

/* ---------- MAIN PAGE ---------- */
export default function Configurator() {
  const [cfg, setCfg] = useState(DEFAULTS);
  const [isOpen, setIsOpen] = useState(false);
  const [autoRotate, setAutoRotate] = useState(false);
  const previewRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const set = (k) => (v) => setCfg((s) => sanitize({ ...s, [k]: v }));

  const summary = useMemo(
    () => [
      { label: "Profile", value: cfg.profile },
      { label: "Type", value: `${cfg.door_type} Door` },
      { label: "Size", value: `${cfg.width} × ${cfg.height} mm` },
      { label: "Panel", value: PANEL_STYLES.find((p) => p.key === cfg.panel_style)?.label },
      { label: "Colour", value: cfg.color },
      { label: "Hardware", value: HARDWARE.find((h) => h.key === cfg.hardware)?.label },
      { label: "Glass", value: cfg.glass_type },
      { label: "Sidelite L", value: cfg.sidelite_left },
      { label: "Sidelite R", value: cfg.sidelite_right },
      { label: "Transom", value: cfg.transom === "Yes" ? `Yes · ${cfg.transomHeight}mm` : "No" },
      { label: "Arched-in-Square", value: cfg.arched_in_square },
      { label: "True Arch", value: cfg.true_arch_frame },
    ],
    [cfg]
  );

  const buildMailto = () => {
    const body = summary.map((s) => `${s.label}: ${s.value}`).join("%0D%0A");
    return `mailto:isaiah@euro-steel.co.nz?subject=Door%20Configurator%20Enquiry&body=${body}`;
  };

  const submitQuote = async (e) => {
    e.preventDefault();
    setError(null);
    const addr = email.trim();
    if (!addr) {
      setError("Please enter your email.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr)) {
      setError("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      // Capture a fresh frame from the 3D canvas as a base64 PNG (no data-URL
      // prefix) so Resend can attach it inline (CID: door-preview).
      let previewImageB64 = null;
      try {
        await new Promise((r) =>
          requestAnimationFrame(() => requestAnimationFrame(r)),
        );
        const canvas = previewRef.current?.querySelector("canvas");
        const dataUrl = canvas?.toDataURL("image/png");
        if (dataUrl && dataUrl.startsWith("data:image/")) {
          previewImageB64 = dataUrl.split(",")[1] || null;
        }
      } catch (_) {
        // best-effort — not fatal if canvas capture fails
      }

      await axios.post(`${BACKEND}/api/quotes`, {
        product_code: "DOOR-CFG",
        product_name: `Door Configurator — ${cfg.door_type} ${cfg.profile}`,
        email: addr,
        phone: phone.trim() || null,
        width_mm: cfg.width,
        height_mm: cfg.height,
        quantity: 1,
        notes: notes.trim() || null,
        spec_summary: summary.map((s) => ({
          label: s.label,
          value: String(s.value ?? ""),
        })),
        preview_image_b64: previewImageB64,
      });
      setSubmitted(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPDF = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      // Give the renderer a moment to draw a fresh frame we can capture
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      const canvas = previewRef.current?.querySelector("canvas");
      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = 210;
      const pageH = 297;
      const margin = 15;

      // Header
      doc.setFillColor(10, 10, 12);
      doc.rect(0, 0, pageW, 22, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text("EUROSTEEL", margin, 14);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.text("Door Configurator Summary", pageW - margin, 14, { align: "right" });

      // Title
      doc.setTextColor(10, 10, 12);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.text("Your Configured Door.", margin, 40);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      const dateStr = new Date().toLocaleDateString("en-NZ", { year: "numeric", month: "long", day: "numeric" });
      doc.text(dateStr, margin, 48);

      // Image (constrained so summary + footer fit on one page)
      let imgY = 56;
      if (canvas) {
        const dataUrl = canvas.toDataURL("image/png");
        const imgW = pageW - margin * 2;
        const imgH = 150; // fixed height so we always leave room for spec + footer
        doc.setDrawColor(220, 220, 220);
        doc.setFillColor(245, 244, 241);
        doc.roundedRect(margin, imgY, imgW, imgH, 3, 3, "FD");
        doc.addImage(dataUrl, "PNG", margin + 5, imgY + 5, imgW - 10, imgH - 10, undefined, "FAST");
        imgY += imgH + 10;
      }

      // Summary block
      doc.setTextColor(10, 10, 12);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("SPECIFICATION", margin, imgY + 4);
      doc.setDrawColor(198, 168, 124);
      doc.setLineWidth(0.5);
      doc.line(margin, imgY + 6, margin + 40, imgY + 6);

      doc.setFontSize(9);
      const rowH = 7;
      const startY = imgY + 14;
      const colW = (pageW - margin * 2) / 2;
      const labelW = 40; // space reserved for the label before the value
      summary.forEach((s, i) => {
        const col = i % 2;
        const row = Math.floor(i / 2);
        const x = margin + col * colW;
        const yy = startY + row * rowH;
        doc.setFont("helvetica", "normal");
        doc.setTextColor(140, 140, 140);
        doc.text(s.label.toUpperCase(), x, yy);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(10, 10, 12);
        const maxValW = colW - labelW - 4;
        const val = doc.splitTextToSize(String(s.value ?? ""), maxValW)[0];
        doc.text(val, x + labelW, yy);
      });

      // Footer
      doc.setDrawColor(230, 230, 230);
      doc.line(margin, pageH - 18, pageW - margin, pageH - 18);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text("isaiah@euro-steel.co.nz  ·  euro-steel.co.nz", margin, pageH - 11);
      doc.text("For larger projects or further customisation, please contact us directly.", margin, pageH - 6);

      const stamp = new Date().toISOString().slice(0, 10);
      doc.save(`eurosteel-door-configurator-${stamp}.pdf`);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("PDF export failed", e);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div data-testid="configurator-page" className="min-h-screen bg-[#f5f4f1] text-[#0a0a0c]">
      {/* Lead time banner */}
      <div
        data-testid="lead-time-banner"
        className="w-full border-b border-black/10 bg-[#0a0a0c] pt-24"
      >
        <div className="mx-auto flex max-w-[1500px] items-center justify-center gap-3 px-6 py-3 text-center lg:px-16">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#c6a87c]" />
          <p className="font-tech text-[10px] font-semibold uppercase tracking-[0.3em] text-white sm:text-[11px]">
            Standard lead time · 16 weeks from order confirmation
          </p>
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#c6a87c]" />
        </div>
      </div>

      {/* Header */}
      <section className="mx-auto max-w-[1500px] px-6 pb-6 pt-10 lg:px-16 lg:pt-16">
        <motion.h1
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.85, delay: 0.05 }}
          className="font-serif-display text-4xl font-bold uppercase leading-[0.98] tracking-tight text-[#0a0a0c] lg:text-6xl"
        >
          Design Your Door.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.75, delay: 0.12 }}
          className="mt-4 max-w-2xl font-body text-[14px] leading-[1.75] text-[#0a0a0c] lg:text-[15px]"
        >
          Sculpt every detail — profile, sidelites, transom, arch, grid, handle and finish. Drag to
          rotate the 3D door, and send your exact spec to our team for a quote.
        </motion.p>

        {/* Contact notice */}
        <Link
          to="/contact"
          data-testid="contact-larger-project-notice"
          className="group mt-6 inline-flex items-center gap-3 rounded-full border border-[#c6a87c]/60 bg-[#c6a87c]/10 px-5 py-2.5 font-tech text-[11px] font-semibold uppercase tracking-[0.22em] text-[#0a0a0c] transition-all duration-300 hover:border-[#c6a87c] hover:bg-[#c6a87c]/20"
        >
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#c6a87c] transition-transform duration-300 group-hover:scale-125" />
          For larger projects or further customisation, contact us directly
          <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
        </Link>
      </section>

      {/* Split layout — preview sticks on iPhone so users can keep eyes on
          the door while scrolling the control dropdowns beneath it. */}
      <section className="mx-auto grid max-w-[1500px] grid-cols-1 gap-6 px-4 pb-24 sm:px-6 lg:grid-cols-[1.05fr_1fr] lg:gap-10 lg:px-16">
        {/* PREVIEW */}
        <div className="sticky top-16 z-30 self-start lg:top-28">
          <div className="relative rounded-[20px] border border-black/10 bg-white p-3 shadow-[0_18px_42px_-24px_rgba(10,10,12,0.35)] sm:rounded-[28px] sm:p-6 sm:shadow-[0_30px_80px_-40px_rgba(10,10,12,0.35)] lg:p-8">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2 sm:mb-4 sm:gap-3">
              <p className="font-tech text-[10px] font-semibold uppercase tracking-[0.26em] text-[#0a0a0c] sm:text-[11px] sm:tracking-[0.3em]">
                Live 3D Preview
              </p>
              <div className="flex gap-1.5 sm:gap-2">
                <button
                  type="button"
                  onClick={() => setIsOpen((v) => !v)}
                  data-testid="configurator-toggle-open"
                  className="rounded-full border border-black/15 px-2.5 py-1 font-tech text-[9px] font-semibold uppercase tracking-[0.2em] text-[#0a0a0c] transition-all duration-300 hover:border-[#c6a87c] hover:bg-[#c6a87c]/10 sm:px-4 sm:py-1.5 sm:text-[10px] sm:tracking-[0.25em]"
                >
                  {isOpen ? (
                    <>Close<span className="hidden sm:inline"> Door</span></>
                  ) : (
                    <>Open<span className="hidden sm:inline"> Door</span></>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setAutoRotate((v) => !v)}
                  data-testid="configurator-toggle-rotate"
                  className={`rounded-full border px-2.5 py-1 font-tech text-[9px] font-semibold uppercase tracking-[0.2em] text-[#0a0a0c] transition-all duration-300 sm:px-4 sm:py-1.5 sm:text-[10px] sm:tracking-[0.25em] ${
                    autoRotate
                      ? "border-[#c6a87c] bg-[#c6a87c]/10"
                      : "border-black/15 hover:border-[#c6a87c] hover:bg-[#c6a87c]/10"
                  }`}
                >
                  {autoRotate ? (
                    <>Stop<span className="hidden sm:inline"> Rotate</span></>
                  ) : (
                    <><span className="hidden sm:inline">Auto </span>Rotate</>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setCfg(DEFAULTS)}
                  data-testid="configurator-reset"
                  className="rounded-full border border-black/15 px-2.5 py-1 font-tech text-[9px] font-semibold uppercase tracking-[0.2em] text-[#0a0a0c] transition-all duration-300 hover:border-[#c6a87c] hover:bg-[#c6a87c]/10 sm:px-4 sm:py-1.5 sm:text-[10px] sm:tracking-[0.25em]"
                >
                  Reset
                </button>
              </div>
            </div>
            <div ref={previewRef} className="relative aspect-[16/11] w-full overflow-hidden rounded-xl bg-[#f5f4f1] sm:aspect-[4/5] sm:rounded-2xl">
              <Viewer3D configuration={cfg} isOpen={isOpen} autoRotate={autoRotate} />
            </div>
            <p className="mt-2 hidden text-center font-tech text-[10px] font-semibold uppercase tracking-[0.3em] text-[#0a0a0c] sm:mt-4 sm:block">
              Drag to rotate · Scroll to zoom
            </p>
          </div>
        </div>

        {/* CONTROLS (categorised) */}
        <div className="flex flex-col gap-6">
          <StructureCard cfg={cfg} set={set} />
          <AdditionsCard cfg={cfg} set={set} />
          <GlazingCard cfg={cfg} set={set} />
          <FinishCard cfg={cfg} set={set} />

          {/* Summary */}
          <div className={`${CardBase} bg-gradient-to-br from-white via-white to-[#f5f4f1]`} data-testid="cat-summary">
            <CategoryTitle eyebrow="05 · Summary" title="Your Configuration." />
            <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {summary.map((s) => (
                <li key={s.label} className="flex items-baseline justify-between gap-3 border-b border-black/10 pb-2">
                  <span className="font-tech text-[10px] font-semibold uppercase tracking-[0.25em] text-[#0a0a0c]/70">
                    {s.label}
                  </span>
                  <span className="font-body text-[13px] font-medium text-[#0a0a0c]">
                    {s.value}
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={downloadPDF}
                disabled={downloading}
                data-testid="configurator-download-pdf"
                className="rounded-full border-2 border-[#0a0a0c] px-8 py-3 font-tech text-[11px] font-semibold uppercase tracking-[0.3em] text-[#0a0a0c] transition-all duration-500 hover:bg-[#0a0a0c] hover:text-white disabled:cursor-wait disabled:opacity-60"
              >
                {downloading ? "Preparing…" : "Download PDF"}
              </button>
              <Link
                to="/contact"
                className="rounded-full border border-black/20 px-8 py-3 font-tech text-[11px] font-semibold uppercase tracking-[0.3em] text-[#0a0a0c] transition-all duration-500 hover:border-[#0a0a0c]"
              >
                Talk to Us
              </Link>
            </div>

            {/* Request Quote form — captures email + optional phone, POSTs
                to /api/quotes so the configuration lands at orders@ via Resend. */}
            {submitted ? (
              <div
                data-testid="configurator-quote-success"
                className="mt-8 rounded-2xl border border-[#c6a87c]/40 bg-[#faf6ec] p-6"
              >
                <p className="font-tech text-[10px] font-bold uppercase tracking-[0.3em] text-[#c6a87c]">
                  Quote sent.
                </p>
                <h4 className="mt-2 font-serif-display text-xl font-bold text-[#0a0a0c]">
                  Thanks — we'll be in touch shortly.
                </h4>
                <p className="mt-2 font-body text-[13px] text-[#0a0a0c]/80">
                  A confirmation copy has been emailed to{" "}
                  <span className="font-semibold">{email}</span>. Our team will reach
                  out from <span className="font-semibold">orders@euro-steel.co.nz</span>{" "}
                  within one business day.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setSubmitted(false);
                    setEmail("");
                    setPhone("");
                    setNotes("");
                  }}
                  data-testid="configurator-quote-success-reset"
                  className="mt-4 font-tech text-[10px] font-bold uppercase tracking-[0.28em] text-[#0a0a0c] underline decoration-[#c6a87c] underline-offset-4 transition-colors hover:text-[#c6a87c]"
                >
                  Submit another configuration →
                </button>
              </div>
            ) : (
              <form
                onSubmit={submitQuote}
                data-testid="configurator-quote-form"
                className="mt-8 space-y-4 rounded-2xl border border-black/10 bg-white p-6"
              >
                <div>
                  <p className="font-tech text-[10px] font-bold uppercase tracking-[0.3em] text-[#c6a87c]">
                    Send this configuration
                  </p>
                  <h4 className="mt-1 font-serif-display text-xl font-bold text-[#0a0a0c]">
                    Request a Quote.
                  </h4>
                  <p className="mt-1 font-body text-[12px] text-[#0a0a0c]/70">
                    Your full specification above goes straight to{" "}
                    <span className="font-semibold">orders@euro-steel.co.nz</span>.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="configurator-email"
                      className="mb-1.5 block font-tech text-[10px] font-bold uppercase tracking-[0.25em] text-[#0a0a0c]"
                    >
                      Email
                    </label>
                    <input
                      id="configurator-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="configurator-quote-email"
                      placeholder="you@company.com"
                      className="h-11 w-full rounded-xl border border-black/15 bg-white px-4 font-body text-[13px] text-[#0a0a0c] shadow-sm transition-all duration-300 focus:border-[#c6a87c] focus:outline-none focus:ring-2 focus:ring-[#c6a87c]/30"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="configurator-phone"
                      className="mb-1.5 block font-tech text-[10px] font-bold uppercase tracking-[0.25em] text-[#0a0a0c]"
                    >
                      Phone <span className="text-[#0a0a0c]/50">(optional)</span>
                    </label>
                    <input
                      id="configurator-phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      data-testid="configurator-quote-phone"
                      placeholder="e.g. 021 234 5678"
                      autoComplete="tel"
                      className="h-11 w-full rounded-xl border border-black/15 bg-white px-4 font-body text-[13px] text-[#0a0a0c] shadow-sm transition-all duration-300 focus:border-[#c6a87c] focus:outline-none focus:ring-2 focus:ring-[#c6a87c]/30"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="configurator-notes"
                    className="mb-1.5 block font-tech text-[10px] font-bold uppercase tracking-[0.25em] text-[#0a0a0c]"
                  >
                    Notes <span className="text-[#0a0a0c]/50">(optional)</span>
                  </label>
                  <textarea
                    id="configurator-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    data-testid="configurator-quote-notes"
                    rows={3}
                    maxLength={1000}
                    placeholder="Site location, install timeframe, anything else we should know…"
                    className="w-full resize-none rounded-xl border border-black/15 bg-white px-4 py-3 font-body text-[13px] text-[#0a0a0c] shadow-sm transition-all duration-300 focus:border-[#c6a87c] focus:outline-none focus:ring-2 focus:ring-[#c6a87c]/30"
                  />
                </div>

                {error && (
                  <p
                    data-testid="configurator-quote-error"
                    className="font-tech text-[11px] font-semibold uppercase tracking-[0.22em] text-red-600"
                  >
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="configurator-request-quote"
                  className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0a0a0c] px-8 py-3.5 font-tech text-[11px] font-semibold uppercase tracking-[0.3em] text-white transition-all duration-500 hover:bg-[#c6a87c] hover:text-[#0a0a0c] disabled:cursor-wait disabled:opacity-60 sm:w-auto"
                >
                  <span className="h-1 w-1 rounded-full bg-[#c6a87c] transition-colors group-hover:bg-[#0a0a0c]" />
                  {submitting ? "Sending…" : "Request Quote"}
                  <span className="h-1 w-1 rounded-full bg-[#c6a87c] transition-colors group-hover:bg-[#0a0a0c]" />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
